<script setup>
import {ref, onMounted, computed, provide} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {formatPrice} from "@/utils/priceFormatter.js";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import {useRouter} from "vue-router";
import SortIcon from "@/components/datatable/sortIcon.vue";
import FiltersModal from "@/views/stocks/orders/FiltersModal.vue";
import Summary from "@/views/stocks/orders/Summary.vue";

const router = useRouter();
const ordersStore = useMainOrdersStore();
const isShowFiltersModal = ref(false);
const keyword = ref('')

const createStockFilters = () => ({
  games: [],
  accounts: [],
  servers: [],
  hasReference: null,
  side: null,
  createdFrom: null,
  createdTo: null,
})

const stockFilters = ref(createStockFilters())
const accountTitleMap = computed(() => Object.fromEntries(ordersStore.gameAccounts.map(acc => [acc.id, acc.title])));
const getAccountTitle = computed(() => (accountId) => accountTitleMap.value[accountId] || 'Disabled Account');
const orders = computed(() => {
  return (ordersStore.stockOrders || []).map(order => ({
    ...order,
    amount: order.amount || order.amount === 0
        ? Number(order.amount)
        : null,
    price: order.price || order.price === 0
        ? Number(order.price)
        : null,
    accountTitle: getAccountTitle.value(order.account),
    value: (order.amount * order.price) || ((order.amount * order.price) === 0)
        ? Number(order.amount * order.price)
        : null,
  }))
});

const normalizeFilters = (filters = {}) => ({
  ...createStockFilters(),
  ...filters,
  games: Array.isArray(filters.games) ? [...filters.games] : [],
  accounts: Array.isArray(filters.accounts) ? [...filters.accounts] : [],
  servers: Array.isArray(filters.servers) ? [...filters.servers] : [],
})

const normalizeValue = (value) => String(value ?? '').trim().toLowerCase()

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== ''

const createSelectedValueSet = (selectedValues) => new Set((selectedValues || []).map(String))

const includesSelectedValue = (selectedValues, value) => !selectedValues.size || selectedValues.has(String(value))

const orderHasReference = (order) => Boolean(order.buyOrderUUID || order.sellOrderUUID)

function matchesKeyword(order, searchTerm) {
  if (!searchTerm) return true

  const searchableValues = [
    order.id,
    order.uuid,
    order.game,
    order.server,
    order.account,
    order.accountTitle,
    order.side,
    order.amount,
    order.price,
    order.value,
    order.buyOrderUUID,
    order.sellOrderUUID,
    order.creationTime,
    order.lastUpdate,
    order.effectiveTime,
    order.notes,
  ]

  return searchableValues.some(value => normalizeValue(value).includes(searchTerm))
}

function filterStockOrders(list, filters, keyword) {
  const normalizedFilters = normalizeFilters(filters)
  const searchTerm = normalizeValue(keyword)
  const selectedGames = createSelectedValueSet(normalizedFilters.games)
  const selectedAccounts = createSelectedValueSet(normalizedFilters.accounts)
  const selectedServers = createSelectedValueSet(normalizedFilters.servers)

  return (list || []).filter(order => {
    if (!includesSelectedValue(selectedGames, order.game)) return false
    if (!includesSelectedValue(selectedAccounts, order.account)) return false
    if (!includesSelectedValue(selectedServers, order.server)) return false

    if (normalizedFilters.hasReference === 'True' && !orderHasReference(order)) return false
    if (normalizedFilters.hasReference === 'False' && orderHasReference(order)) return false
    if (normalizedFilters.side && order.side !== normalizedFilters.side) return false

    const creationTime = Number(order.creationTime)
    const hasCreationTime = hasValue(order.creationTime) && Number.isFinite(creationTime)
    if (hasValue(normalizedFilters.createdFrom) && (!hasCreationTime || creationTime < Number(normalizedFilters.createdFrom))) return false
    if (hasValue(normalizedFilters.createdTo) && (!hasCreationTime || creationTime > Number(normalizedFilters.createdTo))) return false

    return matchesKeyword(order, searchTerm)
  })
}

function uniqueOptionsFromOrders(list, getValue, getLabel = getValue) {
  const seen = new Set()

  return (list || []).reduce((options, order) => {
    const value = getValue(order)
    if (!hasValue(value)) return options

    const key = String(value)
    if (seen.has(key)) return options

    seen.add(key)
    options.push({
      label: hasValue(getLabel(order)) ? String(getLabel(order)) : key,
      value,
    })

    return options
  }, [])
}

const accountOptions = computed(() =>
    uniqueOptionsFromOrders(
        orders.value,
        order => order.account,
        order => order.accountTitle && order.accountTitle !== 'Disabled Account' ? order.accountTitle : order.account
    )
)

const serverOptions = computed(() =>
    uniqueOptionsFromOrders(orders.value, order => order.server)
)

const activeModalFilterCount = computed(() => {
  const filters = normalizeFilters(stockFilters.value)

  return [
    filters.games.length > 0,
    filters.accounts.length > 0,
    filters.servers.length > 0,
    hasValue(filters.hasReference),
    hasValue(filters.side),
    hasValue(filters.createdFrom),
    hasValue(filters.createdTo),
  ].filter(Boolean).length
})

const isModalFilterActive = computed(() => activeModalFilterCount.value > 0)

const filteredOrders = computed(() => filterStockOrders(orders.value, stockFilters.value, keyword.value))

const showFiltersModal = () => {
  isShowFiltersModal.value = true
}

function applyFilters(filters) {
  stockFilters.value = normalizeFilters(filters)
}

function orderDetails({data}) {
  const route = {name: 'stocks-order-details', params: {id: 0}}
  if (data.uuid) {
    route.params.id = data.uuid
  } else {
    route.params.id = data.id
  }
  router.push(route)
}

function onSortChange(params) {
  const sortOrder = params.sortOrder === 1 ? 'asc' : 'desc';
  ordersStore.setCurrencyTransferOrdersSort(params.sortField, sortOrder);
}

onMounted(() => {
  ordersStore.action_getGameAccounts();
  ordersStore.action_getStocksOrders();
})

provide('isShowFiltersModal', isShowFiltersModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs/>
        <div class="flex flex-row justify-between gap-2">
          <div
              class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
            </svg>
          </span>
            <input v-model="keyword" type="text"
                   class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
            <button v-if="keyword"
                    @click="keyword = ''" class="hover:text-red-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="1.5"
                      d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
              </svg>
            </button>
          </div>
          <button @click="showFiltersModal"
                  class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
                  :class="{'border !border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': isModalFilterActive}">
            Filters {{ isModalFilterActive ? '(' + activeModalFilterCount + ')' : '' }}
          </button>
          <Summary :orders="filteredOrders"/>
          <router-link :to="{name: 'stocks-orders-add'}"
                       class="btn btn-outline-dark py-1 text-xs font-medium rounded-md flex items-center h-[32px]">
            New Transfer
          </router-link>
        </div>
      </div>
      <div v-if="ordersStore.isLoading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :value="filteredOrders"
                 scrollable
                 scrollHeight="flex"
                 :virtualScrollerOptions="{ itemSize: 30 }"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed;"
                 :sortField="ordersStore.currencyTransferOrdersSort?.field"
                 :sortOrder="ordersStore.currencyTransferOrdersSort?.type === 'asc' ? 1 : -1"
                 row-hover
                 @row-click="orderDetails"
                 @sort="onSortChange"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column field="id" header="ID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 50px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop.prevent class="w-full block h-full py-[0.375rem]">{{ slotProps.data.id }}</span>
          </template>
        </Column>
        <Column field="game" header="Game" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="server" header="Server" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="accountTitle" header="Account" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="side" header="Side" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="flex flex-row items-center gap-2">
              <span class="inline-flex size-2.5 ring-opacity-25 rounded-full"
                    :class="[slotProps.data.side === 'Withdraw' ? 'bg-[#F44336]' : '',
                  slotProps.data.side === 'Deposit' ? 'bg-[#4CAF50]' : '']">
              </span>
              <span>{{ slotProps.data.side ? slotProps.data.side : '&nbsp' }}</span>
            </span>
          </template>
        </Column>
        <Column field="amount" header="Amount" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="price" header="Price" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.price || slotProps.data.price === 0 ? formatPrice(slotProps.data.price) + ' $' : '' }}
          </template>
        </Column>
        <Column field="value" header="Value" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{ slotProps.data.value || slotProps.data.value === 0 ? formatPrice(slotProps.data.value, 2) + ' $' : '' }}
          </template>
        </Column>
        <Column field="buyOrderUUID" header="Reference" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 8%; min-width: 80px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.buyOrderUUID
                  ? slotProps.data.buyOrderUUID
                  : slotProps.data.sellOrderUUID ? slotProps.data.sellOrderUUID : ''
            }}
          </template>
        </Column>
        <Column field="creationTime" header="Creation Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.creationTime, 'DD MMM YYYY') }}
            </span>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 11%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') }}
            </span>
          </template>
        </Column>
        <Column field="effectiveTime" header="Effective Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 10%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max">
              {{ gregorianDate(slotProps.data.effectiveTime, 'DD MMM HH:mm') }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
    </div>
    <FiltersModal v-if="isShowFiltersModal" :accounts="accountOptions" :servers="serverOptions" :filters="stockFilters"
                  @applyFilter="applyFilters"/>
  </div>
</template>